<template>
    <div
        v-if="showBar"
        :class="['sazka-toast sazka-toast--bottom', { hiding: isSlidingDown }, { active: isSlidingUp }]"
    >
        <div class="bs-container sazka-toast__container">
            <p class="sazka-toast__inner sazka-toast__inner--left">
                {{ barText }} <a class="sazka-toast__link" @click="openRgLimits">ZDE</a>.
            </p>
            <button
                type="button"
                class="sazka-toast__close sazka-toast__close--bottom"
                :class="{ 'sazka-toast__close--bottom-active': showCloseButton }"
                @click="closeBar"
                aria-label="Zavřít notifikaci"
            >
                <DsIcon name="close" :width="16" :height="16" />
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from "vue";
import DsIcon from "@sazka/design-system/src/icon/ds-icon.vue";
import { httpClient } from "@sazka/utils/http-clients/http-client";
import { useSessionStorage } from "@sazka/utils/use-storage";
import { ngFrameworkLoaded } from "../ng-integration";
import { useNgSession } from "../ng-session";
import { isFullAccount } from "../player/player-account";
import { RgLimitsAnalytics, RgFrom } from "./analytics/analytics-rg-limits";
import { GamingLimitsDto } from "./model/gaming-limits";
import { useRgLimitsPopup } from "./use-rg-limits-popup";

const emit = defineEmits(["closeRgLimitsBar", "openRgLimitsBar"]);

const showBar = ref(false);
const showCloseButton = ref(false);
const isSlidingUp = ref(false);
const isSlidingDown = ref(false);
const barText = ref("");
let requiredTimeTimeout = null;
let nonRequiredTimeTimeout = null;

const { isLoggedIn } = useNgSession();

const storage = useSessionStorage();
const storageName = "rg-limits-bar";

interface LimitsBar {
    text: string;
    showtime: number;
}

const REQUIRED_TIME = 5_000;
const NON_REQUIRED_TIME = 5_000;
const COMPENSATION_FOR_RELOAD_TIME = 2_000;
const CLOSING_ANIMATION_TIME = 400;

const getBarText = (limits: GamingLimitsDto) => {
    const limitValues = [
        limits.lossDaily.currentAmount,
        limits.lossMonthly.currentAmount,
        limits.loginCount.currentAmount,
        limits.loginDuration.currentAmount,
        limits.coolOffTime.currentAmount,
        limits.spendingDaily.currentAmount,
        limits.spendingMonthly.currentAmount,
    ];
    if (limitValues.every((l) => l)) {
        return "Máte nastaveny sebeomezující opatření, jejich nastavení můžete změnit";
    }

    if (limitValues.some((l) => l)) {
        return "Máte nastavena pouze některá sebeomezující opatření, jejich nastavení můžete upravit";
    }

    return "Nemáte nastaveny sebeomezující opatření, můžete si je nastavit";
};

const closeBar = () => {
    isSlidingDown.value = true;
    storage.removeItem(storageName);
    clearTimeout(requiredTimeTimeout);
    clearTimeout(nonRequiredTimeTimeout);
    setTimeout(() => {
        emit("closeRgLimitsBar");
        isSlidingDown.value = false;
        showCloseButton.value = false;
    }, CLOSING_ANIMATION_TIME);
};

const openRgLimits = async () => {
    const { showRgPopup } = useRgLimitsPopup();
    RgLimitsAnalytics.pushClickRgLimitsBar(barText.value + " ZDE.");
    await showRgPopup(RgFrom.loginBar);
    closeBar();
};

const startNonRequiredShowTime = () => {
    showCloseButton.value = true;
    storage.removeItem(storageName);
    nonRequiredTimeTimeout = setTimeout(() => {
        closeBar();
    }, NON_REQUIRED_TIME);
};

const openBar = () => {
    isSlidingUp.value = true;
    showBar.value = true;
    storage.setJson<LimitsBar>(storageName, { showtime: Date.now(), text: barText.value }, "essential");

    requiredTimeTimeout = setTimeout(() => {
        startNonRequiredShowTime();
    }, REQUIRED_TIME);
};

const checkForOpenBar = () => {
    const bar = storage.getJson<LimitsBar>(storageName);
    if (!bar) {
        return;
    }

    barText.value = bar.text;
    showBar.value = true;

    const seenMiliseconds = Date.now() - Number(bar.showtime);

    requiredTimeTimeout = setTimeout(() => {
        startNonRequiredShowTime();
    }, REQUIRED_TIME - seenMiliseconds + COMPENSATION_FOR_RELOAD_TIME);
};

checkForOpenBar();

watch(isFullAccount, async (value) => {
    if (value) {
        try {
            emit("openRgLimitsBar");
            const response = await httpClient.get<GamingLimitsDto>("/api/gaming-limits");
            barText.value = getBarText(response.data);
            openBar();
            RgLimitsAnalytics.pushShowRgLimitsBar(barText.value + " ZDE.");
        } catch {
            emit("closeRgLimitsBar");
        }
    }
});

onMounted(async () => {
    await ngFrameworkLoaded();
    watch(isLoggedIn, (value) => {
        if (!value) {
            closeBar();
        } else {
            emit("openRgLimitsBar");
        }
    });
});
</script>
